import { blogCover } from "@/data/blog-covers";
import { Link } from "@tanstack/react-router";
import { BLOG_POSTS, getBlogPost } from "@/data/blog";
import { Calendar, Clock, ChevronLeft, ChevronRight, BookOpen, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedCross } from "@/components/RelatedCross";
import { relatedForBlog } from "@/lib/related";
import EducationalTripleKeyLinks from "@/components/EducationalTripleKeyLinks";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

const SITE = "https://nyclanguageinstitute.com";

/**
 * Body renderer for /blog/$slug. Lives outside the route file so the 1.18 MB
 * article corpus stays in this code-split chunk instead of the critical bundle.
 * The FAQPage JSON-LD is emitted here (still SSR-rendered, still parsed by
 * Google) because route head() no longer has access to the full post.
 */
export function BlogArticleTh({ slug }: { slug: string }) {
  const p = getBlogPost(slug)!;
  const url = `${SITE}/blog/${p.slug}`;

  const currentIdx = BLOG_POSTS.findIndex((x) => x.slug === slug);
  const prev = currentIdx > 0 ? BLOG_POSTS[currentIdx - 1] : null;
  const next = currentIdx < BLOG_POSTS.length - 1 ? BLOG_POSTS[currentIdx + 1] : null;

  const related = p.relatedSlugs
    .map((s) => BLOG_POSTS.find((b) => b.slug === s))
    .filter((x): x is (typeof BLOG_POSTS)[number] => !!x);

  return (
    <main className="min-h-screen bg-white">
      {p.faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "th",
              mainEntity: p.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      )}

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/blog" className="hover:text-gold">Blog</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{p.category}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <BookOpen className="h-3.5 w-3.5" /> {p.category}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">{p.title}</h1>
          <p className="mt-4 text-base text-white/85 sm:text-lg">{p.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-[12px] text-white/70">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> เผยแพร่ {p.publishedAt}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> อัปเดต {p.updatedAt}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.readMinutes} นาที</span>
            <span>By {p.author}</span>
          </div>
          <figure className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/5">
            <img
              src={blogCover(p.category, "th").src}
              alt={blogCover(p.category, "th").altTh}
              width={1200}
              height={630}
              fetchPriority="high"
              decoding="async"
              className="h-auto w-full object-cover"
            />
            <figcaption className="px-4 py-2 text-[11px] text-white/60">
              {blogCover(p.category, "th").altTh}
            </figcaption>
          </figure>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-14">
        {/* TL;DR */}
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">TL;DR · สรุปสั้น</div>
          <ul className="mt-3 space-y-2">
            {p.tldr.map((t) => (
              <li key={t} className="flex items-start gap-2 text-[14px] text-slate-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Sections */}
        <article className="prose prose-slate mt-10 max-w-none prose-headings:font-display prose-headings:text-navy-deep prose-h2:text-2xl prose-h2:font-extrabold prose-p:leading-relaxed prose-strong:text-amber-700">
          {p.sections.map((sec) => (
            <section key={sec.h2} className="mt-8 first:mt-0">
              <h2>{sec.h2}</h2>
              {sec.paragraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              {sec.list && (
                <ul>
                  {sec.list.map((li) => (
                    <li key={li}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </article>

        {/* Service CTA (funnel to canonical landing page) */}
        {p.primaryService && (
          <a
            href={p.primaryService.path}
            className="mt-10 flex items-center justify-between gap-4 rounded-2xl border border-amber-300 bg-amber-50/80 p-5 transition hover:-translate-y-0.5 hover:border-amber-500 hover:shadow-md sm:p-6"
          >
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">บริการที่เกี่ยวข้องโดยตรง</div>
              <div className="mt-1 font-display text-lg font-extrabold text-navy-deep">{p.primaryService.label}</div>
              <div className="mt-1 text-[12px] text-slate-600">ดูขั้นตอน ค่าบริการ และเริ่มงานได้ทันที →</div>
            </div>
            <ChevronRight className="h-6 w-6 shrink-0 text-amber-700" />
          </a>
        )}

        {/* Inline CTA */}
        <div className="mt-12 rounded-2xl border border-navy-deep/15 bg-gradient-to-br from-navy-deep to-[#152043] p-6 text-white sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gold">ต้องการคำปรึกษาเฉพาะเคส?</div>
          <h3 className="mt-2 font-display text-xl font-extrabold sm:text-2xl">
            ปรึกษาฟรี 30 นาที — ทนายและนักแปลของ NYC ตอบใน 15 นาที
          </h3>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>

        {/* FAQ */}
        {p.faqs.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">คำถามที่พบบ่อย</h2>
            <div className="mt-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
                <FaqAccordion items={p.faqs} />
              </div>
            </div>
          </section>
        )}

        {/* Tags */}
        <div className="mt-10 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Tags:</span>
          {p.tags.map((t) => (
            <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700">#{t}</span>
          ))}
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">บทความที่เกี่ยวข้อง</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/blog/$slug"
                  params={{ slug: r.slug }}
                  className="group block rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{r.category}</div>
                  <div className="mt-1.5 font-display text-base font-extrabold leading-snug text-navy-deep group-hover:text-amber-600">{r.title}</div>
                  <div className="mt-1.5 text-[12px] text-slate-500">{r.readMinutes} นาที · {r.updatedAt}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Prev/Next */}
        <nav className="mt-10 flex flex-col gap-3 border-t border-slate-200 pt-8 sm:flex-row sm:justify-between">
          {prev ? (
            <Link to="/blog/$slug" params={{ slug: prev.slug }} className="group flex flex-1 items-start gap-3 rounded-xl border border-slate-200 p-4 transition hover:border-gold/40">
              <ChevronLeft className="mt-0.5 h-5 w-5 text-slate-400 group-hover:text-amber-600" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">บทความก่อนหน้า</div>
                <div className="mt-0.5 text-sm font-bold text-navy-deep group-hover:text-amber-600">{prev.title}</div>
              </div>
            </Link>
          ) : <div className="flex-1" />}
          {next ? (
            <Link to="/blog/$slug" params={{ slug: next.slug }} className="group flex flex-1 items-start justify-end gap-3 rounded-xl border border-slate-200 p-4 text-right transition hover:border-gold/40">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">บทความถัดไป</div>
                <div className="mt-0.5 text-sm font-bold text-navy-deep group-hover:text-amber-600">{next.title}</div>
              </div>
              <ChevronRight className="mt-0.5 h-5 w-5 text-slate-400 group-hover:text-amber-600" />
            </Link>
          ) : <div className="flex-1" />}
        </nav>
      </div>

      <div className="px-4 sm:px-6">
        <ConsultantAdvisoryBlock locale="th" />
      </div>

      {/* Cross-section internal linking graph */}
      <RelatedCross
        bundle={relatedForBlog(p.slug)}
        theme="light"
        heading="เนื้อหาที่เชื่อมโยงกับบทความนี้"
        sub="บริการ บทความ และอุตสาหกรรมที่เกี่ยวข้อง — คลิกเดียวเข้าถึงข้อมูลที่ตรงกับเคสคุณ"
      />

      {/* Money-page deep links */}
      <section className="bg-white px-4 py-2 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <EducationalTripleKeyLinks mode="th" />
        </div>
      </section>

      {/* Trust */}
      <TrustLayer variant="compact" />

      {/* Final CTA */}
      <section className="bg-gradient-to-b from-white to-slate-50 py-14">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            พร้อมเริ่มงานของคุณกับ NYC?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">
            ส่งเอกสารให้ทีมประเมินฟรีภายใน 15 นาที — ทำงาน 365 วัน ครอบคลุม 127 พื้นที่ทั่วไทย
          </p>
          <div className="mt-6 flex justify-center"><ContactCTA /></div>
        </div>
      </section>
    </main>
  );
}

export default BlogArticleTh;
